/**
 * Global SpoilerManager Service
 * Handles auto-collapsing all open dropdown spoiler menus, accordions,
 * native <details> elements, and custom popovers 3 seconds after leaving a page.
 */

type CollapseCallback = () => void;

class SpoilerManagerService {
  private collapseCallbacks: Set<CollapseCallback> = new Set();
  private leaveTimer: number | null = null;

  /**
   * Register a callback to collapse local component spoiler/dropdown states.
   * Returns an unregister function.
   */
  public registerCollapseCallback(cb: CollapseCallback): () => void {
    this.collapseCallbacks.add(cb);
    return () => {
      this.collapseCallbacks.delete(cb);
    };
  }

  /**
   * Collapse all open spoilers immediately across JS state and DOM.
   */
  public collapseAllSpoilers(): void {
    // 1. Clear any pending leave timer
    if (this.leaveTimer !== null) {
      window.clearTimeout(this.leaveTimer);
      this.leaveTimer = null;
    }

    // 2. Execute all registered collapse callbacks
    this.collapseCallbacks.forEach((cb) => {
      try {
        cb();
      } catch (err) {
        console.error('Error executing spoiler collapse callback:', err);
      }
    });

    // 3. Close native HTML <details open> elements in DOM
    if (typeof document !== 'undefined') {
      const openDetails = document.querySelectorAll('details[open]');
      openDetails.forEach((el) => {
        (el as HTMLDetailsElement).open = false;
        el.removeAttribute('open');
      });

      // 4. Blur active select element if open
      if (document.activeElement instanceof HTMLSelectElement || document.activeElement instanceof HTMLButtonElement) {
        document.activeElement.blur();
      }
    }

    // 5. Dispatch global window event for components listening directly
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('collapse-all-spoilers'));
    }
  }

  /**
   * Call when a user leaves the current page/view or tab/window loses visibility/focus.
   * Starts a 3-second timer after which all open spoilers collapse.
   */
  public notifyPageLeave(): void {
    // Clear existing timer if any
    if (this.leaveTimer !== null) {
      window.clearTimeout(this.leaveTimer);
    }

    // Schedule collapse in 3 seconds (3000ms)
    this.leaveTimer = window.setTimeout(() => {
      this.collapseAllSpoilers();
    }, 3000);
  }

  /**
   * Cancel any pending page leave timer (e.g. if user stayed or explicitly reset).
   */
  public cancelPageLeaveTimer(): void {
    if (this.leaveTimer !== null) {
      window.clearTimeout(this.leaveTimer);
      this.leaveTimer = null;
    }
  }
}

export const spoilerManager = new SpoilerManagerService();
